// QuizLibrary.tsx
// Parent portal view of all quizzes created by children

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChildQuizzes } from '../firebase/useQuizzes';
import { Quiz } from '../types';
import './QuizLibrary.css';

interface QuizLibraryProps {
    // For now, we'll use a default child profile ID
    // In production, this would come from parent authentication
    childProfileID?: string;
}

export const QuizLibrary: React.FC<QuizLibraryProps> = ({
    childProfileID = 'demo-child-profile'
}) => {
    const navigate = useNavigate();
    const { quizzes, loading, error } = useChildQuizzes(childProfileID);
    const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
    const [sortBy, setSortBy] = useState<'newest' | 'popular' | 'score'>('newest');

    // Filter quizzes
    const filteredQuizzes = quizzes.filter(quiz => {
        if (filter === 'published') return quiz.isPublished;
        if (filter === 'draft') return quiz.isDraft;
        return true;
    });

    // Sort quizzes
    const sortedQuizzes = [...filteredQuizzes].sort((a, b) => {
        if (sortBy === 'newest') {
            return b.createdAt.getTime() - a.createdAt.getTime();
        }
        if (sortBy === 'popular') {
            return b.attemptCount - a.attemptCount;
        }
        if (sortBy === 'score') {
            return b.averageScore - a.averageScore;
        }
        return 0;
    });

    const handleQuizClick = (quizId: string) => {
        navigate(`/quiz-creator/quiz/${quizId}`);
    };

    if (loading) {
        return (
            <div className="quiz-library">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading quizzes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="quiz-library">
                <div className="error-message">
                    <h3>Error Loading Quizzes</h3>
                    <p>{error.message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="quiz-library">
            <header className="quiz-library-header">
                <h1>Quiz Creator - Parent Portal</h1>
                <p className="subtitle">
                    View and manage quizzes created by your child
                </p>
            </header>

            {/* Stats Overview */}
            <div className="quiz-stats">
                <div className="stat-card">
                    <span className="stat-value">{quizzes.length}</span>
                    <span className="stat-label">Total Quizzes</span>
                </div>
                <div className="stat-card">
                    <span className="stat-value">
                        {quizzes.filter(q => q.isPublished).length}
                    </span>
                    <span className="stat-label">Published</span>
                </div>
                <div className="stat-card">
                    <span className="stat-value">
                        {quizzes.filter(q => q.isDraft).length}
                    </span>
                    <span className="stat-label">Drafts</span>
                </div>
                <div className="stat-card">
                    <span className="stat-value">
                        {quizzes.reduce((sum, q) => sum + q.attemptCount, 0)}
                    </span>
                    <span className="stat-label">Total Attempts</span>
                </div>
            </div>

            {/* Filters and Sorting */}
            <div className="quiz-controls">
                <div className="filter-buttons">
                    <button
                        className={filter === 'all' ? 'active' : ''}
                        onClick={() => setFilter('all')}
                    >
                        All Quizzes
                    </button>
                    <button
                        className={filter === 'published' ? 'active' : ''}
                        onClick={() => setFilter('published')}
                    >
                        Published
                    </button>
                    <button
                        className={filter === 'draft' ? 'active' : ''}
                        onClick={() => setFilter('draft')}
                    >
                        Drafts
                    </button>
                </div>

                <div className="sort-controls">
                    <label>Sort by:</label>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                    >
                        <option value="newest">Newest First</option>
                        <option value="popular">Most Popular</option>
                        <option value="score">Highest Score</option>
                    </select>
                </div>
            </div>

            {/* Quiz Grid */}
            {sortedQuizzes.length === 0 ? (
                <div className="empty-state">
                    <h3>No quizzes found</h3>
                    <p>
                        {filter === 'all'
                            ? 'Your child has not created any quizzes yet.'
                            : `No ${filter} quizzes found.`}
                    </p>
                </div>
            ) : (
                <div className="quiz-grid">
                    {sortedQuizzes.map((quiz) => (
                        <QuizCard
                            key={quiz.id}
                            quiz={quiz}
                            onClick={() => handleQuizClick(quiz.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================
// QUIZ CARD COMPONENT
// ============================================

interface QuizCardProps {
    quiz: Quiz;
    onClick: () => void;
}

const QuizCard: React.FC<QuizCardProps> = ({ quiz, onClick }) => {
    const getStatusBadge = () => {
        if (quiz.reviewStatus === 'flagged') {
            return <span className="badge badge-flagged">Flagged</span>;
        }
        if (quiz.isDraft) {
            return <span className="badge badge-draft">Draft</span>;
        }
        if (quiz.isPublished) {
            return <span className="badge badge-published">Published</span>;
        }
        return null;
    };

    const getDifficultyColor = () => {
        switch (quiz.difficulty) {
            case 'easy': return 'var(--success-color)';
            case 'medium': return 'var(--warning-color)';
            case 'hard': return 'var(--error-color)';
            default: return 'var(--text-secondary)';
        }
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        }).format(date);
    };

    return (
        <div className="quiz-card" onClick={onClick}>
            <div className="quiz-card-header">
                <h3 className="quiz-title">{quiz.title}</h3>
                {getStatusBadge()}
            </div>

            <p className="quiz-description">{quiz.description}</p>

            <div className="quiz-meta">
                <div className="meta-item">
                    <span className="meta-label">Category:</span>
                    <span className="meta-value">{quiz.category}</span>
                </div>
                <div className="meta-item">
                    <span className="meta-label">Difficulty:</span>
                    <span
                        className="meta-value difficulty"
                        style={{ color: getDifficultyColor() }}
                    >
                        {quiz.difficulty}
                    </span>
                </div>
                <div className="meta-item">
                    <span className="meta-label">Questions:</span>
                    <span className="meta-value">{quiz.questionCount}</span>
                </div>
            </div>

            <div className="quiz-stats-mini">
                <div className="stat-mini">
                    <span className="stat-mini-icon">👥</span>
                    <span className="stat-mini-value">{quiz.attemptCount}</span>
                    <span className="stat-mini-label">Attempts</span>
                </div>
                <div className="stat-mini">
                    <span className="stat-mini-icon">✅</span>
                    <span className="stat-mini-value">{quiz.completionCount}</span>
                    <span className="stat-mini-label">Completions</span>
                </div>
                <div className="stat-mini">
                    <span className="stat-mini-icon">📊</span>
                    <span className="stat-mini-value">
                        {quiz.averageScore > 0 ? `${quiz.averageScore.toFixed(1)}%` : 'N/A'}
                    </span>
                    <span className="stat-mini-label">Avg Score</span>
                </div>
            </div>

            <div className="quiz-card-footer">
                <span className="quiz-date">
                    Created {formatDate(quiz.createdAt)}
                </span>
            </div>
        </div>
    );
};
